import React, { Component, createRef } from 'react'
import { Dropdown, Icon, TextArea } from 'semantic-ui-react'
import cn from 'classnames'
import Markdown from '@ui/Markdown'
import TranslationIcon from './TranslationIcon'
import { DELAY } from './constants'
import {
  wantInsertVariable,
  closingVariable,
  updateRowsCount,
  moveToLastCharacter,
  insertVariable,
  searchingOption,
  isEnter,
  removingVariable,
  getVariableName,
  filterOptions,
  moveToCursor,
  moveCursor,
  deleteVariable,
  isDeleting,
  getWidth,
  appendPrefix
} from './utils'

export const INPUT_ID = `editable-input-${Math.random()}`
export const MENU_ID = `editable-input-menu-${Math.random()}`

class EditableField extends Component {
  ref = createRef()
  unmount = false
  state = {}

  componentDidMount() {
    const { text, focus } = this.props
    this.closeOptions()
    this.setState({ text: text || '', editing: !!focus })
  }

  componentDidUpdate(prevProps) {
    const { text, focus } = this.props

    if (text !== prevProps.text && !this.state.editing) {
      this.closeOptions()
      this.setState({ text: text || '' })
    }

    if (!prevProps.focus && focus) {
      this.setState({ editing: focus })
    }
  }

  componentWillUnmount() {
    this.unmount = true
  }

  moveMenu = fitContent => moveToCursor(MENU_ID, INPUT_ID, fitContent)

  previousOption = () => {
    const { currentOptionIndex, options } = this.state
    return currentOptionIndex > 0 ? currentOptionIndex - 1 : options.length - 1
  }

  nextOption = () => {
    const { currentOptionIndex, options } = this.state
    return currentOptionIndex < options.length - 1 ? currentOptionIndex + 1 : 0
  }

  moveOption = code => {
    const isUp = code === 'ArrowUp'
    const currentOptionIndex = isUp ? this.previousOption() : this.nextOption()
    this.setState({
      currentOptionIndex: isUp ? this.previousOption() : this.nextOption(),
      currentOptionValue: this.state.options[currentOptionIndex].value
    })
  }

  handleKey = evt => {
    const { currentOptionValue, insertingVariable, text } = this.state
    if (wantInsertVariable(evt)) {
      this.setState({ insertingVariable: true })
    } else if (insertingVariable) {
      if (closingVariable(evt) || removingVariable(evt, text)) this.closeOptions()

      const searchingOptionCode = searchingOption(evt)
      if (searchingOptionCode) this.moveOption(searchingOptionCode)

      if (isEnter(evt)) {
        evt.stopPropagation()
        evt.preventDefault()
        this.setVariable(currentOptionValue)
        this.setState({ insertingVariable: false })
      }
    } else if (isEnter(evt)) {
      evt.stopPropagation()
      evt.preventDefault()
      this.handleBlur()
    } else if (isDeleting(evt)) {
      this.setState({ deleting: true })
    }
  }

  handleChange = (_, { value }) => {
    let text = value || ''
    let cursorPosition = document.getElementById(INPUT_ID).selectionStart

    if (this.state.deleting) {
      const res = deleteVariable(this.state.text, value, this.props.options)

      if (res) {
        text = res.text
        cursorPosition = res.cursorPosition
      }
    }

    this.setState({ text, cursorPosition, deleting: false }, () => {
      const { onChange, prefix } = this.props
      const { text, cursorPosition } = this.state
      this.resizeTextArea()
      moveCursor(INPUT_ID, cursorPosition)

      if (onChange) onChange(appendPrefix(prefix, text))
    })
  }

  searchOptions = () => {
    const variableName = getVariableName(INPUT_ID)
    const options = filterOptions(this.props.options, variableName)

    this.setState({
      options,
      variableName,
      currentOptionIndex: 0,
      currentOptionValue: options.length ? options[0].value : ''
    })
  }

  handleBlurDropdown = () => this.setState({ selectingFromMenu: true })

  handleBlur = () => {
    setTimeout(() => {
      const { insertingVariable, text, clearing } = this.state
      const { required, onBlur, text: originalText, prefix } = this.props
      const textParsed = text.trim()

      if (clearing) {
        this.setState({ clearing: false })
        const element = document.getElementById(INPUT_ID)
        element.focus()
      } else if (!insertingVariable) {
        const newText = required && !textParsed ? originalText : textParsed
        if (!this.unmount) this.setState({ editing: false, text: newText })
        if (onBlur) onBlur(appendPrefix(prefix, newText))
      }
    }, DELAY)
  }

  handleFocus = () =>
    setTimeout(() => {
      if (!this.state.editing) {
        const text = this.ref.current.children[0]
        const { width } = text.getBoundingClientRect()
        this.setState({ editing: this.props.editable, width })
      }
    }, DELAY)

  handleFocusTextArea = evt => {
    const { onFocus } = this.props
    this.resizeTextArea()
    moveToLastCharacter(evt)
    if (onFocus) onFocus()
  }

  updateMenu = () => {
    if (this.state.insertingVariable) {
      this.moveMenu(this.props.fitContent)
      this.searchOptions()
    }
  }

  resizeTextArea = () => {
    if (this.props.fitContent) {
      const width = getWidth(INPUT_ID) || 4
      this.setState({ width }, this.updateMenu)
    } else {
      updateRowsCount(INPUT_ID)
      this.updateMenu()
    }
  }

  handleClear = () => this.setState({ text: '', clearing: true })

  handleSelectOption = (_, { value }) => this.setState({ currentOptionValue: value })

  setVariable = variableCode => {
    const { onChange, prefix } = this.props
    const { text, variableName, cursorPosition } = this.state
    const { text: newText, position } = insertVariable(
      text,
      variableName,
      variableCode,
      cursorPosition
    )

    if (!this.unmount) {
      this.setState(
        { text: newText, editing: true, cursorPosition: position },
        () => {
          this.resizeTextArea()
          moveCursor(INPUT_ID, position)
        }
      )
    }

    if (onChange) onChange(appendPrefix(prefix, newText))
  }

  handleClose = evt =>
    setTimeout(() => {
      if (evt) this.setVariable(this.state.currentOptionValue)
      else {
        if (!this.state.selectingFromMenu) this.setState({ editing: false })
        this.setState({ selectingFromMenu: false })
        this.closeOptions()
      }
    }, DELAY)

  closeOptions = () => {
    const { options = [] } = this.props

    this.setState({
      insertingVariable: false,
      currentOptionIndex: 0,
      currentOptionValue: options.length > 0 ? options[0].value : '',
      variableName: '',
      options
    })
  }

  render() {
    const {
      editable,
      className,
      size,
      light,
      bold,
      blue,
      hideBorder,
      placeholder,
      translationTooltip,
      fitContent,
      clearable,
      prefix
    } = this.props
    const { text, editing, insertingVariable, currentOptionIndex, options, width } =
      this.state

    return (
      <div
        ref={this.ref}
        className={cn('editable-field', className, {
          editable,
          editing,
          'hide-border': hideBorder
        })}
        onClick={this.handleFocus}
        onBlur={this.handleBlur}
      >
        {editing ? (
          <>
            {prefix && (
              <span
                className={cn('editable-field__prefix', size, { light, bold, blue })}
              >
                {prefix}
              </span>
            )}
            <TextArea
              id={INPUT_ID}
              onChange={this.handleChange}
              value={text}
              className={cn('editable-input basic width full', size, {
                light,
                bold,
                blue
              })}
              onFocus={this.handleFocusTextArea}
              onKeyDown={this.handleKey}
              maxLength={200}
              rows={fitContent ? 1 : undefined}
              style={{ width: fitContent ? `${width}px` : undefined }}
              autoFocus
            />
            {clearable && !!text && (
              <Icon
                name='times'
                onClick={this.handleClear}
                className='clear-button'
              />
            )}
            <TranslationIcon tooltip={translationTooltip} />
            <Dropdown
              id={MENU_ID}
              className='variables-menu-options'
              options={options}
              onChange={this.handleSelectOption}
              onClose={this.handleClose}
              onBlur={this.handleBlurDropdown}
              value={options.length && options[currentOptionIndex].value}
              open={insertingVariable}
              selectOnBlur={false}
            />
          </>
        ) : (
          <Markdown className={cn(size, { light, bold, blue })}>
            {prefix ? `${prefix}${text || placeholder}` : text || placeholder}
          </Markdown>
        )}
      </div>
    )
  }
}

export default EditableField
